import { CheckCircleSolid, InfoCircleSolid, TriangleExclamationSolid } from "@bonusx-global/bx-icons";
import ReportIcon from "@mui/icons-material/Report";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import GlobalStyles from "@mui/material/GlobalStyles";
import SnackbarContent from "@mui/material/SnackbarContent";
import { useAtomValue } from "jotai";
import type { CustomContentProps, SnackbarOrigin, VariantType } from "notistack";
import { SnackbarProvider, useSnackbar } from "notistack";
import type { JSXElementConstructor, PropsWithChildren, ReactNode } from "react";
import { forwardRef } from "react";
import { useDialog } from "../../hooks/useDialog";
import R from "../../R";
import { snackBarStore } from "../../stores/snackbar";
import { CrossIcon } from "../BonusXIcons/CrossIcon";
import { EsclmationMarkIcon } from "../BonusXIcons/EsclmationMarkIcon";

export function SnackbarDialog() {
  const state = useAtomValue(snackBarStore);
  const mlDialog = useDialog();

  return (
    <Dialog
      open={!!state.open}
      onClose={mlDialog.hideModal}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <div css={{ textAlign: "right" }} onClick={() => mlDialog.hideModal()}>
        <CrossIcon />
      </div>
      <DialogContent
        css={{
          textAlign: "center",
          "@media only screen and (max-width:500px)": { paddingLeft: 0, paddingRight: 0, overflow: "unset" },
        }}
      >
        <EsclmationMarkIcon width="50" height="50" />
      </DialogContent>
      <DialogTitle
        css={{
          textAlign: "center",
          "@media only screen and (max-width:500px)": { paddingLeft: 0, paddingRight: 0 },
        }}
        id="alert-dialog-title"
      >
        {state.title}
      </DialogTitle>
      <DialogContent
        css={{ "@media only screen and (max-width:500px)": { paddingLeft: 0, paddingRight: 0, overflow: "unset" } }}
      >
        {typeof state.message === "string" ? (
          <DialogContentText id="alert-dialog-description" dangerouslySetInnerHTML={{ __html: state.message }} />
        ) : (
          state.message
        )}
      </DialogContent>
      <DialogActions css={{ margin: "auto" }}>
        {state.positiveButton || (
          <Button
            autoFocus
            variant="contained"
            css={{ textTransform: "none" }}
            color="primary"
            onClick={() => {
              if (state.onPositiveClick && state.onPositiveClick()) {
                return;
              }
              mlDialog.hideModal();
            }}
          >
            {state.positiveText}
          </Button>
        )}
        {state.negativeButton ||
          (state.onNegativeClick && (
            <Button
              color="primary"
              onClick={() => {
                state.onNegativeClick?.();
                mlDialog.hideModal();
              }}
            >
              {state.negativeText}
            </Button>
          ))}
      </DialogActions>
      <DialogContent
        css={{
          textAlign: "center",
          color: R.colors.vividBlue,
          "@media only screen and (max-width:500px)": { paddingLeft: 0, paddingRight: 0, overflow: "unset" },
        }}
        onClick={() => {
          state.onExitClick?.();
          mlDialog.hideModal();
        }}
      >
        {state.exitText}
        <span css={{ paddingLeft: 5 }}>{state.exitIcon}</span>
      </DialogContent>
    </Dialog>
  );
}

const SnackbarAlert = forwardRef<HTMLDivElement, CustomContentProps>(function SnackbarAlert(props, ref) {
  // NOTE: For some reason `props.onClose` is not provided by notistack
  const { closeSnackbar } = useSnackbar();

  if (props.variant === "default") {
    return <SnackbarContent ref={ref} message={props.message} action={props.action} />;
  }

  return (
    <Alert
      ref={ref}
      elevation={6}
      variant="filled"
      severity={props.variant}
      iconMapping={props.iconVariant}
      action={props.action}
      onClose={() => closeSnackbar(props.id)}
    >
      {props.message}
    </Alert>
  );
});

const snackbarProviderComponents: Record<VariantType, JSXElementConstructor<CustomContentProps>> = {
  default: SnackbarAlert,
  success: SnackbarAlert,
  error: SnackbarAlert,
  info: SnackbarAlert,
  warning: SnackbarAlert,
};

const snackbarProviderAnchorOrigin: SnackbarOrigin = {
  vertical: "bottom",
  horizontal: "right",
};

const snackbarProviderGlobalStyles = (
  <GlobalStyles
    styles={(theme) => ({
      ".notistack-SnackbarContainer": {
        [theme.breakpoints.down("md")]: {
          marginBottom: theme.spacing(9),
        },
      },
    })}
  />
);

const snackbarProviderIconVariant: Partial<Record<VariantType, ReactNode>> = {
  info: <InfoCircleSolid />,
  warning: <TriangleExclamationSolid />,
  success: <CheckCircleSolid />,
  error: <ReportIcon />,
};

export function MLDialogProvider(props: PropsWithChildren<{}>) {
  return (
    <SnackbarProvider
      autoHideDuration={3000}
      anchorOrigin={snackbarProviderAnchorOrigin}
      iconVariant={snackbarProviderIconVariant}
      Components={snackbarProviderComponents}
    >
      {snackbarProviderGlobalStyles}
      <SnackbarDialog />
      {props.children}
    </SnackbarProvider>
  );
}
